import React from "react";
import dayjs from "dayjs";
import admissionIcon from "../../../assets/receptionistIcons/ipBilling/admission.svg";
import statusIcon from "../../../assets/receptionistIcons/ipBilling/status.svg";
import billAmountIcon from "../../../assets/receptionistIcons/ipBilling/billAmount.svg";
import balanceIcon from "../../../assets/receptionistIcons/ipBilling/balance.svg";
import styles from "./billSummary.module.css";
import { formatMoney } from "../common/money";

const STATUS_LABEL = {
  pending: "Pending",
  partial_payment: "Partial",
  paid: "Paid",
  cancelled: "Cancelled",
};

/* The figure reads better with the sign held off the number. */
const spaceOutCurrency = (text) =>
  typeof text === "string"
    ? text.replace(/([+-])?₹\s*/, (_, sign) => (sign ? `${sign} ₹ ` : "₹ "))
    : text;

const Tile = ({ icon, label, value, sub, tone, valueClass = "", meter }) => (
  <article className={`${styles.tile} ${tone ? styles[tone] : ""}`.trim()}>
    <div className={styles.top}>
      <img src={icon} alt="" className={styles.ico} />
      <p className={styles.label}>{label}</p>
    </div>

    <p className={`${styles.value} ${valueClass}`.trim()}>{spaceOutCurrency(value)}</p>

    {meter != null || sub ? (
      <div className={styles.foot}>
        {meter != null ? (
          <span className={`${styles.meter} ${meter >= 99.5 ? styles.meterOk : ""}`.trim()}>
            <i style={{ width: `${meter}%` }} />
          </span>
        ) : null}
        {sub ? <p className={styles.sub}>{sub}</p> : null}
      </div>
    ) : null}
  </article>
);

const PharmacyBillSummaryCards = ({ bill }) => {
  const billDate = bill?.created_at;
  // Server-computed: nets out both payments received and any approved returns, rather than
  // just grand_total - paid_amount, so an approved return correctly shows up here too.
  const balance = Number(bill?.due_amount || 0);
  const status = bill?.status || "pending";
  const net = Number(bill?.grand_total || 0);
  const collected = Number(bill?.paid_amount || 0);
  const collectedPct = net ? Math.min(100, Math.max(0, (collected / net) * 100)) : 0;

  const balanceDisplay =
    balance > 0
      ? { text: formatMoney(balance), className: styles.valueDue }
      : balance < 0
        ? { text: `+${formatMoney(Math.abs(balance))}`, className: styles.valueCredit }
        : { text: formatMoney(0), className: "" };

  return (
    <div className={styles.band}>
      <Tile
        icon={admissionIcon}
        label="Bill date"
        value={billDate ? dayjs(billDate).format("D MMM YYYY") : "—"}
        sub={billDate ? "Raised at this counter" : "—"}
      />
      <Tile
        icon={statusIcon}
        label="Status"
        tone={status === "paid" ? "tOk" : status === "cancelled" ? undefined : "tWarn"}
        value={STATUS_LABEL[status] || status}
        valueClass={status === "paid" ? styles.valueCredit : ""}
        /* What is left to do about it, not what kind of sale it was - the sale
           type is already a tag in the heading above. */
        sub={
          balance > 0
            ? collected > 0
              ? `${formatMoney(collected)} collected so far`
              : "Nothing collected yet"
            : "Settled in full"
        }
      />
      <Tile
        icon={billAmountIcon}
        label="Bill amount"
        value={formatMoney(net)}
        sub="Inclusive of GST"
      />
      <Tile
        icon={balanceIcon}
        label="Balance"
        tone={balance > 0.009 ? "tWarn" : "tOk"}
        value={balanceDisplay.text}
        valueClass={balanceDisplay.className}
        meter={collectedPct}
        sub={`${formatMoney(collected)} of ${formatMoney(net)} collected`}
      />
    </div>
  );
};

export default PharmacyBillSummaryCards;
